/**
 * Template Build Streaming API
 *
 * RTK Query API with fakeBaseQuery for SSE streaming using @microsoft/fetch-event-source
 * Handles streaming communication with the template build chat backend.
 */

import type { AGUISSEEvent, SSEPerformanceMetrics, StepPayload } from '@features/ai-chat-core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { Mutex } from 'async-mutex';

import type {
  DraftTemplate,
  TemplateBuildChatRequest,
  TemplateBuildState,
  TemplateBuildStreamData,
} from '../types';

import {
  appendStreamingContent,
  clearAllActivities,
  clearInterrupt,
  completeWorkflow,
  finalizeMessage,
  markDraftSaved,
  setActivitySnapshot,
  setAssistantStatus,
  setCreatedTemplateId,
  setCurrentStep,
  setDraftTemplate,
  setError,
  setNeedsStatusRefetch,
  setSSEPerformanceMetrics,
  setThreadId,
  showInterrupt,
  startStreamingMessage,
} from './chatSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

/**
 * Extended SSE Event type for template build chat
 */
type TemplateBuildSSEEvent = AGUISSEEvent | {
  type: 'INTERRUPT';
  interrupt?: {
    id: string;
    payload: {
      eventType: string;
      confirmLabel?: string;
      cancelLabel?: string;
      message?: string;
      question?: string;
      options?: Array<{ id: string; label: string; description?: string }>;
      allowMultiple?: boolean;
      namespace?: string;
      confirmationType?: string;
      steps?: StepPayload[];
      metadata?: unknown;
    };
  };
};

// ============================================================================
// Auth Token Management
// ============================================================================

const mutex = new Mutex();

// ============================================================================
// API Definition
// ============================================================================

export const templateBuildStreamingApi = createApi({
  reducerPath: 'templatesAiEdit/streaming',
  baseQuery: fakeBaseQuery(),
  endpoints: builder => ({
    /**
     * Stream template build chat messages with interrupt workflow (AG-UI Protocol)
     */
    streamTemplateBuildChat: builder.query<TemplateBuildStreamData, TemplateBuildChatRequest>({
      queryFn: () => ({
        data: {
          isActive: false,
          isDone: false,
        },
      }),

      async onCacheEntryAdded(
        payload,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheEntryRemoved, dispatch, getState },
      ) {
        const controller = new AbortController();

        const taskId = payload.taskId;

        // Performance tracking
        const requestStartTime = Date.now();
        const runId = payload.runId;

        let connectionOpenTime: number | null = null;
        let firstEventTime: number | null = null;
        let firstTokenTime: number | null = null;
        let streamEndTime: number | null = null;
        let eventCount = 0;
        let outcome: 'success' | 'interrupt' | 'error' = 'success';
        let metricsDispatched = false;

        const thresholdMs = parseInt(
          import.meta.env.VITE_SSE_PERFORMANCE_THRESHOLD_MS || '10000',
          10,
        );

        const dispatchPerformanceMetrics = () => {
          if (metricsDispatched) return;
          if (!connectionOpenTime || !firstEventTime || !streamEndTime) return;

          metricsDispatched = true;

          const metrics: SSEPerformanceMetrics = {
            time_to_connection_ms: connectionOpenTime - requestStartTime,
            time_to_first_event_ms: firstEventTime - requestStartTime,
            time_to_first_token_ms: firstTokenTime
              ? firstTokenTime - requestStartTime
              : firstEventTime - requestStartTime,
            total_duration_ms: streamEndTime - requestStartTime,
            run_id: runId,
            thread_id: payload.threadId || undefined,
            outcome,
            event_count: eventCount,
            exceeded_threshold: streamEndTime - requestStartTime > thresholdMs,
            threshold_ms: thresholdMs,
          };

          dispatch(setSSEPerformanceMetrics(metrics));
        };

        await mutex.waitForUnlock();

        let token: string;
        try {
          token = await getAuth0AccessTokenSilently();
        } catch (error) {
          console.error('Failed to get access token for streaming:', error);
          dispatch(setError('Authentication failed. Please log in again.'));
          outcome = 'error';
          streamEndTime = Date.now();
          dispatchPerformanceMetrics();
          return;
        }

        // Internal map to track tool calls for logging
        const toolCallMap = new Map<string, { toolCallName: string; args: string }>();

        (async () => {
          try {
            await fetchEventSource(
              `${API_BASE_URL}/v2/provider/notetemplates/build/${taskId}/messages`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
                signal: controller.signal,

                async onopen(res) {
                  if (!res.ok) {
                    const errorText = await res.text();
                    if (res.status === 401) {
                      if (!mutex.isLocked()) {
                        const release = await mutex.acquire();
                        try {
                          dispatch(
                            setError('Session expired. Please refresh the page and log in again.'),
                          );
                        } finally {
                          release();
                        }
                      } else {
                        await mutex.waitForUnlock();
                        dispatch(setError('Session expired. Please try again or refresh the page.'));
                      }
                    } else {
                      dispatch(setError(`Failed to connect: ${res.status} ${res.statusText}`));
                    }

                    updateCachedData(draft => {
                      draft.isActive = false;
                    });

                    throw new Error(`Stream failed with status ${res.status}: ${errorText}`);
                  }

                  console.debug('📡 [SSE] Template build chat connection opened', {
                    taskId,
                    threadId: payload.threadId,
                  });

                  if (!connectionOpenTime) {
                    connectionOpenTime = Date.now();
                  }

                  updateCachedData(draft => {
                    draft.isActive = true;
                    draft.isDone = false;
                  });
                },

                onmessage: event => {
                  if (!event.data) return;

                  if (!firstEventTime) {
                    firstEventTime = Date.now();
                  }
                  eventCount++;

                  let data: TemplateBuildSSEEvent;
                  try {
                    data = JSON.parse(event.data) as TemplateBuildSSEEvent;
                  } catch {
                    console.warn('Failed to parse SSE event:', event.data);
                    return;
                  }

                  console.debug('📡 [SSE] Template build chat event received:', {
                    type: data.type,
                    keys: Object.keys(data),
                  });

                  switch (data.type) {
                    case 'RUN_STARTED': {
                      const state = getState() as Record<string, unknown>;
                      const templateBuildState = state['templatesAiEdit/ui'] as TemplateBuildState | undefined;
                      const currentThreadId = templateBuildState?.threadId;
                      const newThreadId = 'threadId' in data ? data.threadId : null;
                      const isNewThread = newThreadId && newThreadId !== currentThreadId;

                      if (newThreadId) {
                        dispatch(setThreadId(newThreadId));
                      }
                      dispatch(setAssistantStatus('thinking'));

                      if (isNewThread) {
                        console.debug('📡 [AG-UI] New template build thread started');
                        dispatch(clearAllActivities());
                      }

                      const currentInterrupt = templateBuildState?.interrupt;
                      if (currentInterrupt?.eventType === 'CONFIRMATION') {
                        console.debug('📡 [AG-UI] Clearing CONFIRMATION interrupt on RUN_STARTED');
                        dispatch(clearInterrupt());
                      }
                      break;
                    }

                    case 'STEP_STARTED': {
                      const stepName = 'step_name' in data ? data.step_name : '';
                      if (stepName) {
                        dispatch(setCurrentStep(stepName));
                      }
                      break;
                    }

                    case 'ACTIVITY_SNAPSHOT': {
                      const activityData = data as any;
                      
                      // Handle DRAFT_TEMPLATE activity type
                      if (activityData.activityType === 'DRAFT_TEMPLATE') {
                        // Accept both camelCase (SSE) and snake_case (REST API) field names
                        const templateData = activityData.content as {
                          title: string;
                          description?: string | null;
                          sections: Array<{
                            id: string;
                            title: string;
                            order: number;
                            content: string;
                            exampleOutput?: string | null;   // camelCase from SSE
                            example_output?: string | null;  // snake_case fallback
                          }>;
                        };
                        
                        const draftTemplate: DraftTemplate = {
                          title: templateData.title,
                          description: templateData.description ?? null,
                          sections: templateData.sections.map(s => ({
                            id: s.id,
                            title: s.title,
                            order: s.order,
                            content: s.content,
                            exampleOutput: s.exampleOutput ?? s.example_output ?? null,
                          })),
                        };
                        
                        dispatch(setDraftTemplate(draftTemplate));
                        console.debug('📡 [SSE] Draft template updated:', {
                          title: draftTemplate.title,
                          sectionCount: draftTemplate.sections.length,
                        });
                      } else if (activityData.activityType === 'TEMPLATE_CREATED') {
                        // Handle template creation completion
                        const createdData = activityData.content as { templateId: string };
                        if (createdData.templateId) {
                          // Record the target and treat the draft as published,
                          // but leave the session open — the provider keeps the
                          // preview and the chat (GDN-3099).
                          dispatch(setCreatedTemplateId(createdData.templateId));
                          dispatch(markDraftSaved());
                          console.debug('📡 [SSE] Template created:', createdData.templateId);
                        }
                      } else {
                        // Standard activity snapshot
                        dispatch(
                          setActivitySnapshot({
                            messageId: activityData.messageId,
                            activityType: activityData.activityType,
                            message: activityData.content?.message || '',
                            timestamp: activityData.timestamp,
                            replace: activityData.replace,
                          }),
                        );
                      }
                      break;
                    }

                    case 'TEXT_MESSAGE_START': {
                      const messageId = 'messageId' in data ? data.messageId : crypto.randomUUID();
                      dispatch(startStreamingMessage(messageId || crypto.randomUUID()));
                      break;
                    }

                    case 'TEXT_MESSAGE_CONTENT': {
                      const delta = 'delta' in data ? data.delta : '';
                      if (delta) {
                        dispatch(appendStreamingContent(delta));
                        if (!firstTokenTime) {
                          firstTokenTime = Date.now();
                        }
                      }
                      break;
                    }

                    case 'TEXT_MESSAGE_END': {
                      dispatch(finalizeMessage());
                      break;
                    }

                    case 'INTERRUPT': {
                      const interruptData = (data as any).interrupt;
                      if (interruptData) {
                        dispatch(
                          showInterrupt({
                            interruptId: interruptData.id,
                            eventType: interruptData.payload.eventType,
                            confirmLabel: interruptData.payload.confirmLabel,
                            cancelLabel: interruptData.payload.cancelLabel,
                            message: interruptData.payload.message,
                            question: interruptData.payload.question,
                            options: interruptData.payload.options,
                            allowMultiple: interruptData.payload.allowMultiple,
                            steps: interruptData.payload.steps as StepPayload[],
                            metadata: interruptData.payload.metadata,
                          }),
                        );
                        outcome = 'interrupt';
                      }
                      break;
                    }

                    case 'RUN_FINISHED': {
                      const runFinishedData = data as any;

                      // Check if the run finished with an interrupt
                      if (runFinishedData.outcome === 'interrupt' && runFinishedData.interrupt) {
                        const interruptData = runFinishedData.interrupt;
                        dispatch(
                          showInterrupt({
                            interruptId: interruptData.id,
                            eventType: interruptData.payload.eventType,
                            confirmLabel: interruptData.payload.confirmLabel,
                            cancelLabel: interruptData.payload.cancelLabel,
                            message: interruptData.payload.message,
                            question: interruptData.payload.question,
                            options: interruptData.payload.options,
                            allowMultiple: interruptData.payload.allowMultiple,
                            namespace: interruptData.payload.namespace,
                            confirmationType: interruptData.payload.confirmationType,
                            steps: interruptData.payload.steps as StepPayload[],
                            metadata: interruptData.payload.metadata,
                          }),
                        );
                        outcome = 'interrupt';
                        console.debug('📡 [SSE] RUN_FINISHED with interrupt:', {
                          interruptId: interruptData.id,
                          eventType: interruptData.payload.eventType,
                        });
                      } else {
                        // Normal completion - trigger status refetch to get latest from Postgres
                        dispatch(completeWorkflow());
                        dispatch(setNeedsStatusRefetch(true));
                        outcome = 'success';
                        console.debug('📡 [SSE] RUN_FINISHED with success, triggering status refetch');
                      }

                      updateCachedData(draft => {
                        draft.isActive = false;
                        draft.isDone = true;
                      });
                      streamEndTime = Date.now();
                      dispatchPerformanceMetrics();
                      break;
                    }

                    case 'TOOL_CALL_START': {
                      const toolCallId =
                        'toolCallId' in data ? data.toolCallId : crypto.randomUUID();
                      const toolCallName =
                        'toolCallName' in data ? data.toolCallName : 'unknown';
                      const args = 'args' in data ? JSON.stringify(data.args) : '{}';

                      toolCallMap.set(toolCallId, { toolCallName, args });
                      break;
                    }

                    case 'TOOL_CALL_END': {
                      const toolCallId = 'toolCallId' in data ? data.toolCallId : '';
                      if (toolCallId) {
                        const toolCall = toolCallMap.get(toolCallId);
                        if (toolCall) {
                          console.debug('📡 [AG-UI] Tool call completed:', {
                            toolCallId,
                            toolCallName: toolCall.toolCallName,
                          });
                          toolCallMap.delete(toolCallId);
                        }
                      }
                      break;
                    }

                    default:
                      console.debug('📡 [SSE] Unhandled event type:', (data as any).type);
                  }
                },

                onerror: err => {
                  console.error('📡 [SSE] Stream error:', err);
                  dispatch(setError('Connection error. Please try again.'));
                  outcome = 'error';
                  streamEndTime = Date.now();
                  dispatchPerformanceMetrics();

                  updateCachedData(draft => {
                    draft.isActive = false;
                    draft.isDone = true;
                  });

                  throw err;
                },
              },
            );
          } catch (error) {
            console.error('📡 [SSE] Stream failed:', error);
            dispatch(setError('Failed to connect to chat service. Please try again.'));
            outcome = 'error';
            streamEndTime = Date.now();
            dispatchPerformanceMetrics();

            updateCachedData(draft => {
              draft.isActive = false;
              draft.isDone = true;
            });
          }
        })();

        await cacheEntryRemoved;
        controller.abort();
      },
    }),
  }),
});

export const { useStreamTemplateBuildChatQuery } = templateBuildStreamingApi;

